/**
 * Featured review card on the product page.
 *
 * The Opinew app block embeds the product's reviews as JSON on
 * #opinew-plugin[data-opw-prodreviews], further down the page. This picks one and fills in the
 * card in the info column. If nothing suitable is found the card keeps whatever the theme
 * editor's manual quote put there, or stays hidden when there is no manual quote.
 *
 * Picking is deliberately conservative: a high star rating does not mean the text is positive
 * (a 5-star review can still warn about something), so very short and very long bodies are
 * skipped and a specific review can be pinned by id from the theme editor.
 */
(() => {
  const MIN_BODY = 40;   // shorter than this says nothing useful ("like the product")
  const MAX_BODY = 240;  // longer than this overflows the card and tends to ramble

  const readReviews = () => {
    // The app renders two #opinew-plugin nodes with identical data; either will do.
    for (const el of document.querySelectorAll('[data-opw-prodreviews]')) {
      try {
        const data = JSON.parse(el.getAttribute('data-opw-prodreviews'));
        const groups = [data?.reviews?.highest_rated, data?.reviews?.content, data?.reviews?.recent];
        const all = [];
        const seen = new Set();
        groups.forEach((group) => {
          if (!Array.isArray(group)) return;
          group.forEach((r) => {
            if (r && !seen.has(r.id)) { seen.add(r.id); all.push(r); }
          });
        });
        if (all.length) return all;
      } catch (e) { /* malformed payload: try the next node */ }
    }
    return [];
  };

  const choose = (reviews, minStars, pinId) => {
    if (pinId) {
      const pinned = reviews.find((r) => String(r.id) === String(pinId));
      if (pinned) return pinned;
    }

    const usable = reviews.filter((r) => {
      const body = (r.body || '').trim();
      return body
        && !r.by_shop_owner
        && Number(r.star_rating) >= minStars
        && body.length >= MIN_BODY
        && body.length <= MAX_BODY;
    });
    if (!usable.length) return null;

    // Highest rating first, then verified, then the longest body inside the window above.
    usable.sort((a, b) =>
      Number(b.star_rating) - Number(a.star_rating)
      || Number(!!b.verified_review) - Number(!!a.verified_review)
      || (b.body || '').trim().length - (a.body || '').trim().length);
    return usable[0];
  };

  const fill = (card, review) => {
    const stars = Math.max(0, Math.min(5, Math.round(Number(review.star_rating) || 0)));
    const body = (review.body || '').trim();
    const name = (review.user_name || '').trim() || 'Verified buyer';

    const textEl = card.querySelector('[data-pdp-review-text]');
    const metaEl = card.querySelector('[data-pdp-review-meta]');
    const avatarEl = card.querySelector('[data-pdp-review-avatar]');
    const starsEl = card.querySelector('[data-pdp-review-stars]');

    if (textEl) textEl.textContent = `"${body}"`;
    if (avatarEl) avatarEl.textContent = name.charAt(0).toUpperCase();
    if (metaEl) {
      metaEl.textContent = review.verified_review ? `${name} · Verified buyer` : name;
    }
    if (starsEl) {
      starsEl.setAttribute('aria-label', `${stars} out of 5 stars`);
      [...starsEl.querySelectorAll('svg')].forEach((svg, i) => {
        svg.classList.toggle('is-empty', i >= stars);
      });
    }
    card.hidden = false;
  };

  const init = () => {
    const cards = document.querySelectorAll('[data-pdp-review]');
    if (!cards.length) return false;

    const reviews = readReviews();
    if (!reviews.length) return false;

    cards.forEach((card) => {
      const review = choose(reviews, Number(card.dataset.minStars) || 5, card.dataset.pinId);
      if (review) fill(card, review);
    });
    return true;
  };

  // The Opinew block sits below this card in the DOM, so on a fast first paint its JSON may not
  // be parsed yet. Retry briefly, then give up and leave the manual quote (or hidden card) alone.
  if (!init()) {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (init() || tries >= 20) clearInterval(timer);
    }, 250);
  }
})();
